import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { createDefaultSettings } from '@/db/defaults'
import { executeDatabaseOperation } from '@/db/errors'
import { settingsSchema } from '@/db/schemas'
import type { AuditLog } from '@/db/types'
import { inspectStorage } from '@/db/services/storageService'

export type BackupReminderReason = 'never' | 'older_than_7_days' | 'ten_completed' | 'storage_70'

export interface BackupStatus {
  pending: boolean
  lastBackupAt?: string
  shouldRemind: boolean
  reasons: BackupReminderReason[]
  completedSinceBackup: number
  dismissedUntil?: string
}

const cachedStatuses = new WeakMap<RealMachinesDatabase, Promise<BackupStatus>>()
const DISMISS_KEY = 'real-maquinas:backup-reminder-dismissed-until:v1'

function isBackupTimestampAudit(log: AuditLog): boolean {
  if (log.entityType !== 'settings') return false
  const fields = log.changes['fields']
  return Array.isArray(fields) && fields.length === 1 && fields[0] === 'lastBackupAt'
}

async function calculateBackupStatus(database: RealMachinesDatabase): Promise<BackupStatus> {
  return executeDatabaseOperation('verificar situação do backup', async () => {
    const settings = settingsSchema.parse(
      (await database.settings.get('app')) ?? createDefaultSettings(),
    )

    const [changeAfterBackup, storage, completedSinceBackup] = await Promise.all([
      settings.lastBackupAt
        ? database.auditLogs
            .where('occurredAt')
            .above(settings.lastBackupAt)
            .filter((log) => !isBackupTimestampAudit(log))
            .first()
        : Promise.resolve(undefined),
      inspectStorage(undefined, false),
      settings.lastBackupAt
        ? database.auditLogs
            .where('[entityType+occurredAt]')
            .between(['inspection', settings.lastBackupAt], ['inspection', '\uffff'], false, true)
            .filter((log) => log.action === 'complete')
            .count()
        : database.inspections.where('status').equals('completed').count(),
    ])
    const reasons: BackupReminderReason[] = []
    if (!settings.lastBackupAt) reasons.push('never')
    if (
      settings.lastBackupAt &&
      Date.now() - new Date(settings.lastBackupAt).getTime() > 7 * 24 * 60 * 60 * 1000
    ) {
      reasons.push('older_than_7_days')
    }
    if (completedSinceBackup >= 10) reasons.push('ten_completed')
    if (storage.warning || storage.critical) reasons.push('storage_70')
    const dismissedUntilValue =
      typeof localStorage === 'undefined' ? null : localStorage.getItem(DISMISS_KEY)
    const dismissedUntil =
      dismissedUntilValue && Number.isFinite(new Date(dismissedUntilValue).getTime())
        ? dismissedUntilValue
        : undefined
    const dismissed = Boolean(dismissedUntil && new Date(dismissedUntil).getTime() > Date.now())

    return {
      pending: !settings.lastBackupAt || Boolean(changeAfterBackup),
      ...(settings.lastBackupAt ? { lastBackupAt: settings.lastBackupAt } : {}),
      shouldRemind: reasons.length > 0 && !dismissed,
      reasons,
      completedSinceBackup,
      ...(dismissedUntil ? { dismissedUntil } : {}),
    }
  })
}

export class BackupStatusService {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  getStatus(): Promise<BackupStatus> {
    const cached = cachedStatuses.get(this.database)
    if (cached) return cached

    const status = calculateBackupStatus(this.database).catch((error: unknown) => {
      cachedStatuses.delete(this.database)
      throw error
    })
    cachedStatuses.set(this.database, status)
    return status
  }

  dismissReminder(hours = 24) {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DISMISS_KEY, new Date(Date.now() + hours * 60 * 60 * 1000).toISOString())
    }
    invalidateBackupStatus(this.database)
  }
}

export function invalidateBackupStatus(database: RealMachinesDatabase = getDatabase()) {
  cachedStatuses.delete(database)
}
